// StockDetails.tsx
import React from "react";
import { MdStore, MdBlock } from "react-icons/md";
import styles from "./StockDetails.module.scss";
import { useTheme } from "../hooks/useTheme";
import { StockStore, ItemStop } from "../types/types";
import { AvailabilityStatus } from "../types/enums";
import { useStockDetails } from "./useStockDetails";

interface StockDetailsProps {
  stockStores?: StockStore[];
  stockEnabled: boolean;
  title?: string;
  forceStore?: boolean;
  itemStops?: ItemStop[];
  selectedStoreIds: number[];
  availabilityStatus?: AvailabilityStatus;
}

export const StockDetails = ({
  stockStores,
  stockEnabled,
  title = "Наличие",
  forceStore = false,
  itemStops = [],
  selectedStoreIds,
  availabilityStatus,
}: StockDetailsProps) => {
  const { theme } = useTheme();

  const {
    availableStores,
    availabilityByStores,
    totalAvailable,
    showNoStoreMessage,
    showUnavailableMessage,
  } = useStockDetails({
    stockStores,
    stockEnabled,
    forceStore,
    itemStops,
    selectedStoreIds,
    availabilityStatus,
  });

  if (showNoStoreMessage) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.card }}>
        <div className={styles.header}>
          <MdBlock size={20} color={theme.primary} />
          <span className={styles.title} style={{ color: theme.text }}>
            Доступность
          </span>
        </div>
        <div
          className={styles.noStoreMessage}
          style={{ color: theme.textSecondary }}
        >
          Выберите точку
        </div>
      </div>
    );
  }

  if (showUnavailableMessage) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.card }}>
        <div className={styles.header}>
          <MdBlock size={20} color={theme.danger} />
          <span className={styles.title} style={{ color: theme.text }}>
            Доступность
          </span>
        </div>
        <div className={styles.noStoreMessage} style={{ color: theme.danger }}>
          Товар недоступен в выбранных точках
        </div>
      </div>
    );
  }

  // Остатки — показываем даже если одна точка
  if (stockEnabled && availableStores.length > 0) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.card }}>
        <div className={styles.header}>
          <MdStore size={20} color={theme.primary} />
          <span className={styles.title} style={{ color: theme.text }}>
            {title}
          </span>
          <span className={styles.total} style={{ color: theme.primary }}>
            {totalAvailable} шт
          </span>
        </div>

        {availableStores.map((store, index) => (
          <div key={index} className={styles.storeRow}>
            <span className={styles.storeName} style={{ color: theme.text }}>
              {store.name}
            </span>
            <div className={styles.storeStock}>
              <span
                className={styles.storeAvailable}
                style={{ color: theme.success }}
              >
                {store.available} шт
              </span>
              {store.total > 0 && (
                <span
                  className={styles.storeTotal}
                  style={{ color: theme.textSecondary }}
                >
                  / {store.total}
                </span>
              )}
            </div>
          </div>
        ))}

        {totalAvailable === 0 && (
          <span className={styles.outOfStock} style={{ color: theme.danger }}>
            Товар временно отсутствует
          </span>
        )}
      </div>
    );
  }

  // Стоп-листы — показываем только если больше одной точки
  if (
    !stockEnabled &&
    availabilityByStores.length > 0 &&
    itemStops.length > 1
  ) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.card }}>
        <div className={styles.header}>
          <MdStore size={20} color={theme.primary} />
          <span className={styles.title} style={{ color: theme.text }}>
            Доступность
          </span>
        </div>

        {availabilityByStores.map((store, index) => (
          <div key={index} className={styles.storeRow}>
            <span className={styles.storeName} style={{ color: theme.text }}>
              {store.name}
            </span>
            <div className={styles.storeStock}>
              <span
                className={
                  store.available ? styles.available : styles.unavailable
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: store.available ? theme.success : theme.danger,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: store.available
                      ? theme.success
                      : theme.danger,
                  }}
                />
                {store.available ? "Доступен" : "Недоступен"}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};
